/**
 * ISSUE-1365. Share a game from inside the chat composer, after v1's
 * "Matches" picker: pick a league and a day, tick up to five games, send.
 * Each game goes out as its own `gameCard` message through `/message/send`,
 * the same contract the Scores "Share to a chat" dialog uses: the server
 * freezes the card itself, so this file sends only event ids.
 *
 * Renders through the shared `Dialog` (a bottom sheet under 768px).
 */
import { useEventList } from "@sportsmet/events-sdk/private";
import { useMessageSend } from "@sportsmet/messaging-sdk/private";
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import {
  Button,
  Checkbox,
  Dialog,
  FOCUS_RING,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../../errors/apiErrorBus";
import { shiftDay, todayInZone } from "../scores/calendarDay";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { buildShareRequestId } from "../shareGame/shareChatLogic";
import {
  buildGamePickerLeagues,
  GAME_PICKER_DESCRIPTION,
  GAME_PICKER_EMPTY,
  GAME_PICKER_FAILED,
  GAME_PICKER_LEAGUE_LABEL,
  GAME_PICKER_NEXT_DAY,
  GAME_PICKER_NO_LEAGUES,
  GAME_PICKER_PREVIOUS_DAY,
  GAME_PICKER_TITLE,
  gamePickerDayLabel,
  gamePickerMatchup,
  gamePickerSendLabel,
  toggleGamePick,
} from "./gamePickerLogic";

const STEP_CLASS = `flex min-h-touch-target min-w-touch-target items-center justify-center rounded-md text-body-strong text-primary hover:bg-surface-secondary ${FOCUS_RING}`;

function GameList({
  leagueId,
  day,
  timeZone,
  picked,
  onToggle,
}: {
  leagueId: string;
  day: string;
  timeZone: string;
  picked: readonly string[];
  onToggle: (eventId: string) => void;
}): ReactNode {
  const read = useEventList({ leagueId, day, timeZone });
  if (read.isLoading) {
    return (
      <div className="flex flex-col gap-2" data-testid="game-picker-loading">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }
  if (read.isError || extractErrorCode(read.data) !== null) {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="game-picker-failed"
      >
        {GAME_PICKER_FAILED}
      </p>
    );
  }
  const events = read.data?.data?.events ?? [];
  if (events.length === 0) {
    return (
      <p
        className="text-body text-ink-secondary"
        data-testid="game-picker-empty"
      >
        {GAME_PICKER_EMPTY}
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-2" data-testid="game-picker-games">
      {events.map((event) => (
        <li
          key={event.eventId}
          data-testid={`game-picker-game-${event.eventId}`}
        >
          <Checkbox
            label={gamePickerMatchup({ away: event.away, home: event.home })}
            description={event.statusLabel}
            checked={picked.includes(event.eventId)}
            onCheckedChange={() => {
              onToggle(event.eventId);
            }}
          />
        </li>
      ))}
    </ul>
  );
}

export function GamePickerSheet({
  chatId,
  open,
  onOpenChange,
}: {
  chatId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const timeZone = resolveViewerTimeZone();
  const today = todayInZone({ nowMs: Date.now(), timeZone });
  const catalog = useLeagueCatalogGet({}, { enabled: open });
  const profile = useProfileGet({}, { enabled: open });
  const send = useMessageSend();

  const leagues = buildGamePickerLeagues({
    sports: catalog.data?.data?.sports ?? [],
    followedLeagueIds: profile.data?.data?.profile?.followedLeagueIds ?? [],
  });
  const [chosenLeagueId, setChosenLeagueId] = useState<string | null>(null);
  const [day, setDay] = useState(today);
  const [picked, setPicked] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const leagueId =
    chosenLeagueId !== null &&
    leagues.some((league) => league.leagueId === chosenLeagueId)
      ? chosenLeagueId
      : (leagues[0]?.leagueId ?? null);

  const close = (): void => {
    setPicked([]);
    setDay(today);
    setChosenLeagueId(null);
    onOpenChange(false);
  };

  // One message per game, in the order they were ticked. A refusal stops the
  // run and says why; what already went out stays sent and is unticked.
  const sendPicked = async (): Promise<void> => {
    if (picked.length === 0 || isSending) return;
    setIsSending(true);
    try {
      for (const eventId of picked) {
        const result = await send.mutateAsync({
          chatId,
          requestId: buildShareRequestId(),
          kind: "gameCard",
          gameCardEventId: eventId,
          timeZone,
        });
        if (showApiError(result)) return;
        // Untick each game once it is out, so a retry after a refusal sends
        // only the games that did not go.
        setPicked((current) => current.filter((id) => id !== eventId));
      }
      close();
    } catch {
      showOfflineError();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={GAME_PICKER_TITLE}
      description={GAME_PICKER_DESCRIPTION}
      autoFocus={false}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button
            data-testid="game-picker-send"
            disabled={picked.length === 0}
            loading={isSending}
            onClick={() => {
              void sendPicked();
            }}
          >
            {gamePickerSendLabel(picked.length)}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {catalog.isLoading ? (
          <Skeleton shape="control" label="Loading leagues" />
        ) : leagues.length === 0 || leagueId === null ? (
          <p className="text-body text-ink-secondary">
            {GAME_PICKER_NO_LEAGUES}
          </p>
        ) : (
          <>
            <Select
              label={GAME_PICKER_LEAGUE_LABEL}
              items={leagues.map((league) => ({
                value: league.leagueId,
                label: league.label,
              }))}
              value={leagueId}
              onValueChange={setChosenLeagueId}
            />
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                aria-label={GAME_PICKER_PREVIOUS_DAY}
                data-testid="game-picker-previous-day"
                className={STEP_CLASS}
                onClick={() => {
                  setDay((current) =>
                    shiftDay({ day: current, deltaDays: -1 }),
                  );
                }}
              >
                ‹
              </button>
              <span
                className="text-body-strong text-ink"
                data-testid="game-picker-day"
                aria-live="polite"
              >
                {gamePickerDayLabel(day, today)}
              </span>
              <button
                type="button"
                aria-label={GAME_PICKER_NEXT_DAY}
                data-testid="game-picker-next-day"
                className={STEP_CLASS}
                onClick={() => {
                  setDay((current) => shiftDay({ day: current, deltaDays: 1 }));
                }}
              >
                ›
              </button>
            </div>
            <GameList
              leagueId={leagueId}
              day={day}
              timeZone={timeZone}
              picked={picked}
              onToggle={(eventId) => {
                setPicked((current) => toggleGamePick(current, eventId));
              }}
            />
          </>
        )}
      </div>
    </Dialog>
  );
}
